import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { SelectChangeEvent, Typography } from '@mui/material'
import { useFormikContext } from 'formik'
import { useSelector } from 'react-redux'
import { FormErrorMessage } from 'src/components/shared/form/FormErrorMessage'
import { FormikSelect } from 'src/components/shared/form/formik/FormikSelect'
import { FormikTextField } from 'src/components/shared/form/formik/FormikTextField'
import type { GigsafePermission } from 'src/interfaces/gigsafeUser'
import { getGigsafeRoles, getGigsafeRolesLoading } from 'src/selectors/gigsafeUser'
import { humanizeSnakeCase } from 'src/utils/humanizeSnakeCase'
import { GigsafePermissionsField } from './GigsafePermissionsField'
import { useGigsafeRoleGate } from './useGigsafeRoleGate'
import type { GigsafeRoleFormValues } from './GigsafeRoleForm.interface'

const CREATE_ROLE_LABEL = 'Create new role'
// Read-only, the blank option cannot create anything — it is just "nothing selected".
const SELECT_ROLE_LABEL = 'Select a role'

interface Props {
  // Without `customer_roles_manage`: the select still browses roles, name and permissions are
  // disabled, and the modal renders no submit.
  isReadOnly: boolean
  onRoleChange(event: SelectChangeEvent<string>): void
}

const GigsafeRoleFieldsComponent = ({ isReadOnly, onRoleChange }: Props) => {
  const { values, touched, errors, setFieldValue } = useFormikContext<GigsafeRoleFormValues>()

  const roles = useSelector(getGigsafeRoles)
  const rolesLoading = useSelector(getGigsafeRolesLoading)
  const { isDefaultRole } = useGigsafeRoleGate()
  const blankRoleLabel = isReadOnly ? SELECT_ROLE_LABEL : CREATE_ROLE_LABEL
  const isLocked = isDefaultRole || isReadOnly

  // The blank `value: ''` option is a real selectable value (create mode), not a placeholder —
  // matching `FlowEditorStepGroupSettingsPanel`'s `{ label: 'None', value: '' }` pattern, which is
  // why `placeholder` below is also set to the same label.
  const roleOptions = [
    { label: blankRoleLabel, value: '' },
    ...roles.map((role) => {
      return { label: humanizeSnakeCase(role.name), value: role.id }
    }),
  ]

  // Formik marks every field touched on submit, so this only shows after a failed save attempt —
  // matching `OAuthScopesField`'s `touched.scopes && typeof errors.scopes === 'string'` gate.
  const permissionsError =
    touched.permissions && typeof errors.permissions === 'string' ? errors.permissions : null

  const handlePermissionsChange = useCallback(
    (permissions: GigsafePermission[]) => {
      setFieldValue('permissions', permissions)
    },
    [setFieldValue],
  )

  return (
    <div css={tw`flex flex-col gap-4`}>
      <FormikSelect<GigsafeRoleFormValues>
        name="roleId"
        label="Role"
        placeholder={blankRoleLabel}
        options={roleOptions}
        disabled={rolesLoading}
        onChange={onRoleChange}
        data-testid="gigsafe-role-select"
      />
      <FormikTextField<GigsafeRoleFormValues>
        name="name"
        label="Role name *"
        disabled={isLocked}
        data-testid="gigsafe-role-name-input"
      />
      {isDefaultRole ? (
        <Typography variant="body2" css={tw`text-shades-500`}>
          Built-in role. Its name and permissions can&apos;t be changed.
        </Typography>
      ) : null}
      <div>
        <GigsafePermissionsField
          value={values.permissions}
          disabled={isLocked}
          onChange={handlePermissionsChange}
        />
        <FormErrorMessage error={permissionsError} css={tw`mb-0 mt-2 text-left`} />
      </div>
    </div>
  )
}

export const GigsafeRoleFields = memo(GigsafeRoleFieldsComponent)
